import type { ConnectedSummaryLine } from '../../../../../components/ConnectedSummaryLines'
import { ConnectedSummaryLinesV2 } from '../../../../../components/ConnectedSummaryLinesV2'
import { EventNames } from '../../../../../../../types/misc.types'
import { lightImpactVibrate, postCTA, trackEvent } from '../../../../../../../utils/appBridge'
import {
  isSipIdealPlansFetchFailed,
  isSipIdealPlansLoading,
  useSipPlansContext
} from '../../SipPlansProvider'
import { PlansScreen, PlansSipListEventIds } from '../../../../analytics/plansEvents'
import { HeaderHeroDescription } from '../../../../lumpsum/plans-list/lumpsum-ui-components/HeaderHeroDescription'
import { SipHeaderHeroSkeleton } from '../../sip-ui-components/SipHeaderHeroSkeleton'
import { HeaderRiveBackdrop } from '../../../../lumpsum/plans-list/header/common/HeaderRiveBackdrop'
import {
  getPlansFlowTheme,
  PlansThemeFlow,
  resolveHeaderAppearanceWithRiskProfile,
  type LumpsumHeaderAppearance
} from '../../../../lumpsum/plans-list/header/common/riskProfileHeaderTheme'
import type { AllocationRiskProfile } from '../../../../../expert-review/types/allocation.types'
import type { DerivedIdealCategory } from '../../deriveSipPlansView'
import { SipPlanMode } from '../../../api/types'
import { isSipIdealPlansListFullyEmpty } from '../../../../utils/sip/planListUtils'

const SIP_IDEAL_HEADER_DESCRIPTION =
  'This is what your SIP portfolio looks like when it\'s personalised to your life stage and risk appetite. '

export function SipIdealHeader() {
  const { planMode, plansOverview, derivedPlans, plansQuery } = useSipPlansContext()
  const headerAppearance = resolveSipIdealHeaderAppearance(plansOverview?.riskProfile)
  const isHeaderActive = planMode === SipPlanMode.IDEAL_SIPS
  const isHeaderLoading = isSipIdealPlansLoading(
    SipPlanMode.IDEAL_SIPS,
    plansOverview != null,
    plansQuery
  )
  const isPlansEmpty =
    !isHeaderLoading && isSipIdealPlansListFullyEmpty(derivedPlans?.idealCategories)
  const isPlansFetchFailed = isSipIdealPlansFetchFailed(SipPlanMode.IDEAL_SIPS, plansQuery)

  if (isPlansEmpty || isPlansFetchFailed) {
    return null
  }

  const plansListHero = getSipIdealHeroFields(
    derivedPlans?.idealCategories,
    plansOverview?.idealFundCount
  )
  const connectedSummaryLines = isHeaderLoading
    ? []
    : getSipIdealConnectedSummaryLines(plansListHero)

  function handleStrategyLandingCtaClick() {
    const strategyLandingCta = plansOverview?.strategyLandingCta
    if (strategyLandingCta == null) return
    lightImpactVibrate()
    trackEvent({
      eventName: EventNames.UserClicked,
      payload: {
        ScreenName: PlansScreen.SipIdeal,
        eventId: PlansSipListEventIds.StrategyLandingCta,
        buttonLabel: 'Strategy landing'
      }
    })
    postCTA(JSON.stringify(strategyLandingCta))
  }

  return (
    <div>
      {isHeaderLoading ? (
        <SipHeaderHeroSkeleton backgroundColor={headerAppearance.backgroundColor} isIdeal />
      ) : (
        <div className="relative overflow-visible px-5 pt-6 pb-1">
          <HeaderRiveBackdrop
            isActive={isHeaderActive}
            riveSrc={headerAppearance.riveSrc}
            headerSvgSrc={headerAppearance.headerSvgSrc}
            backgroundColor={headerAppearance.backgroundColor}
            onLandingCtaClick={
              plansOverview?.strategyLandingCta == null
                ? undefined
                : handleStrategyLandingCtaClick
            }
          />

          <div className="relative z-[1] flex gap-3 pointer-events-none">
            <div className="min-w-0 flex-1 max-w-[calc(100%-130px)] pointer-events-auto">
              <div className="text-[22px] font-[500] leading-[130%] text-[#232323]">
                Ideal SIPs
              </div>

              {connectedSummaryLines.length > 0 ? (
                <div className="pt-3 pl-[2px]">
                  <ConnectedSummaryLinesV2
                    lines={connectedSummaryLines}
                    connectorColor={headerAppearance.connectorColor}
                    connectorThickness={2}
                    gap={4}
                  />
                </div>
              ) : null}

              <HeaderHeroDescription
                description={SIP_IDEAL_HEADER_DESCRIPTION}
                maxLines={3}
                className="max-w-[230px]"
              />
            </div>
          </div>
        </div>
      )}
    </div>
  )
}

type SipIdealHeroFields = {
  categoryCount: number
  sipCount: number
}

function getSipIdealHeroFields(
  idealCategories: DerivedIdealCategory[] | undefined,
  idealFundCount?: number | null
): SipIdealHeroFields {
  const categoriesWithSips = (idealCategories ?? []).filter(
    (category) => (category?.idealSips ?? []).length > 0
  )
  const sipCountFromCategories = categoriesWithSips.reduce(
    (sipTotal, category) => sipTotal + (category?.idealSips ?? []).length,
    0
  )
  const overviewIdealFundCount = idealFundCount ?? 0

  return {
    categoryCount: categoriesWithSips.length,
    sipCount: overviewIdealFundCount > 0 ? overviewIdealFundCount : sipCountFromCategories
  }
}

function getSipIdealConnectedSummaryLines(
  plansListHero: SipIdealHeroFields
): ConnectedSummaryLine[] {
  const lines: ConnectedSummaryLine[] = []

  if (plansListHero.categoryCount > 0) {
    lines.push({
      html: `<span class="font-[500] text-[#3d3d3d]">${formatCategoryCountLabel(plansListHero.categoryCount)}</span>`
    })
  }

  if (plansListHero.sipCount > 0) {
    lines.push({
      html: `<span class="font-[500] text-[#3d3d3d]">${formatSipCountLabel(plansListHero.sipCount)}</span>`
    })
  }

  return lines
}

function formatCategoryCountLabel(categoryCount: number): string {
  return `${categoryCount} categor${categoryCount === 1 ? 'y' : 'ies'}`
}

function formatSipCountLabel(sipCount: number): string {
  return `${sipCount} SIP${sipCount === 1 ? '' : 's'}`
}

const SIP_IDEAL_FLOW_THEME = getPlansFlowTheme(PlansThemeFlow.SIP_IDEAL)
const SIP_IDEAL_HEADER_STYLE = {
  backgroundColor: SIP_IDEAL_FLOW_THEME.topColor,
  chromeBackground: SIP_IDEAL_FLOW_THEME.chromeBackground,
  reviewedOnClassName: SIP_IDEAL_FLOW_THEME.periodLabelClassName,
  connectorColor: SIP_IDEAL_FLOW_THEME.connectorColor
}

export function resolveSipIdealHeaderAppearance(
  riskProfile?: AllocationRiskProfile | null
): LumpsumHeaderAppearance {
  return resolveHeaderAppearanceWithRiskProfile({
    riskProfile,
    strategyPalette: 'ideal',
    ...SIP_IDEAL_HEADER_STYLE
  })
}
